import {
   Box,
   FormControl,
   FormControlLabel,
   Grid,
   Radio,
   RadioGroup,
   Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
   StyledDateContainer,
   CustomDatePicker,
   CustomCalendarTodayOutlinedIcon,
   CommonButton,
   CustomTextFeild,
   CustomSelect,
   CustomMenuItem,
   CustomRequiredInputLabel,
   CommonOutlinedButton,
   CustomLeftIcon,
} from '../../common/StyledComponents/StyledFields';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import { object, string, ValidationError } from 'yup';
import styles from './DailyActivitiesForm.module.scss';
import DailyActivityInformation from './DailyActivityInformation';
import HcccTimePicker from '../../common/customTimePicker/hcccTimePicker';
import SuccessPopup from '../../devotee/donation/successPopup';
import { apiRequest, catchErrorMessageHandler } from '../../../infrastructure/backendService';
import LOCALE from '../../../config/en-us.json';
import toast, { Toaster } from 'react-hot-toast';
import ErrorPopup from '../../common/ErrorPopup';
import constants from '../../../config/constants.json';
import apiPath from '../../../config/apiPaths.json';
import { isNil } from 'lodash';
import commonStyles from '../../../App.module.scss';
import { ClipLoader } from 'react-spinners';


interface DailyActivityFormProps {
   handleBack?: () => void;
   selectedData?: any;
   setCloseForm?: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ErrorMessageTypes {
   activityName: string;
   activityId: string;
   categoryCode: string;
   scheduleDate: string;
   scheduleTime: string;
   activityDescription: string;
   deity: string;
   spopportunity: string;
   activityfrequency: string;
   activityType: string;
}

const DailyActivitiesForm = (props: DailyActivityFormProps): JSX.Element => {
   const { selectedData } = props || {};
   const formFields = {
      activityName: '',
      activityId: '',
      categoryCode: '',
      scheduleDate: '',
      scheduleTime: '',
      activityDescription: '',
      deity: '',
      spopportunity: '',
      activityfrequency: '',
      activityType: '',
   };
   const [errorMessage, setErrorMessage] = useState<ErrorMessageTypes>({
      ...formFields,
   });
   const [submitLoading, setSubmitLoading] = useState(false);
   const [isOpen, setIsOpen] = useState<boolean>(false);
   const [isAdding, setIsAdding] = useState(false);
   const [deitysName, setDeitysName] = useState([]);
   const [formData, setFormData] = useState({
      ...formFields,
   });

   const updateData = () => {
      const formatDate = (dateString: any) => {
         const date = new Date(dateString);
         const month = (date.getMonth() + 1).toString().padStart(2, '0');
         const day = date.getDate().toString().padStart(2, '0');
         const year = date.getFullYear();
         return `${month}-${day}-${year}`;
      };
      setFormData({
         activityName: selectedData?.activityName || '',
         activityId: selectedData?.activityId || '',
         categoryCode: selectedData?.activityCategoryCode || '',
         scheduleDate: formatDate(selectedData?.scheduleDate) || '',
         scheduleTime: selectedData?.activityStartTime
            ? selectedData.activityStartTime.split(':').slice(0, 2).join(':') +
            ' ' +
            selectedData.activityStartTime.slice(-2)
            : '', // Trim seconds and keep AM/PM
         activityDescription: selectedData?.activityDescription || '',
         deity: selectedData?.deityId || '',
         spopportunity: selectedData?.spOpportunity || '',
         activityfrequency: selectedData?.activityFrequency || '',
         activityType: selectedData?.activityType || '',
      });
   };
   useEffect(() => {
      updateData();
   }, [selectedData]);

   useEffect(() => {
      searchApiCall();
   }, []);
   const validationSchema = object().shape({
      activityName: string().required('Activity Name is required'),
      activityId: string().required('Activity Id is required'),
      categoryCode: string().required('Category Code is required'),
      scheduleDate: string().required('Schedule Date is required'),
      scheduleTime: string().required('Schedule Time is required'),
      activityDescription: string().required('Activity Description is required'),
      spopportunity: string().required('Sp Opportunity is required'),
      deity: string().required('Deity is required'),
      activityfrequency: string().required('Activity Frequency is required'),
      activityType: string().required('Activity Type is required'),
   });
   const updateDailyActivityApiCall = () => {
      setSubmitLoading(true);
      const formatDate = (dateString: any) => {
         const date = new Date(dateString);
         const month = (date.getMonth() + 1).toString().padStart(2, '0');
         const day = date.getDate().toString().padStart(2, '0');
         const year = date.getFullYear();
         return `${year}-${month}-${day}`;
      };
      const reqBody = {
         deityId: formData.deity.toString(),
         scheduleDate: formatDate(formData.scheduleDate),
         activityId: formData.activityId,
         activityName: formData.activityName,
         activityDescription: formData.activityDescription,
         activityType: formData.activityType,
         activityCategoryCode: formData.categoryCode,
         activityFrequency: formData.activityfrequency,
         activityStartTime: formData.scheduleTime,
         spOpportunity: formData.spopportunity,
         activityEndTime: null,
         deityGroupId: null,
         id: selectedData?.id,
      };
      apiRequest({
         method: 'PUT',
         endpoint: `${apiPath.addedTempleActivity}/${selectedData?.id}`,
         body: JSON.stringify(reqBody),
         includeToken: true,
      })
         .then((res) => {
            const successMessage = LOCALE.DAILY_ACTIVITY;
            toast.custom(
               <SuccessPopup
                  headerMessage="Details Update"
                  showMessage={successMessage}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
               {
                  duration: constants.TOAST_POPUP_DURATION,
               },
            );
            setSubmitLoading(false);
         })
         .catch((err) => {
            toast.custom(
               <ErrorPopup
                  showMessage={catchErrorMessageHandler(err)}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
               {
                  duration: constants.TOAST_POPUP_DURATION,
               },
            );
            setSubmitLoading(false);
         });
   };

   const handleUpdate = async (): Promise<void> => {
      const errors: ErrorMessageTypes = { ...formFields, activityfrequency: '' };
      try {
         // Validate the form data against the validation schema
         await validationSchema.validate(formData, { abortEarly: false });
         // If validation passes, you can submit the form
         setErrorMessage(errors);
         // Call your API or perform other actions here
         updateDailyActivityApiCall();
      } catch (error: any) {
         // Handle validation errors

         if (error instanceof ValidationError) {
            error.inner.forEach((e: any) => {
               if (e.path) {
                  errors[e.path as keyof ErrorMessageTypes] = e.message;
               }
            });
         }
         setErrorMessage(errors);
      }
   };

   const handleSave = async (): Promise<void> => {
      const errors: ErrorMessageTypes = { ...formFields };
      try {
         // Validate the form data against the validation schema
         await validationSchema.validate(formData, { abortEarly: false });
         // If validation passes, you can submit the form
         setErrorMessage(errors);
         // Call your API or perform other actions here
         saveApiCall();
         setFormData(formFields);
      } catch (error: any) {
         // Handle validation errors
         if (error instanceof ValidationError) {
            error.inner.forEach((e: any) => {
               if (e.path) {
                  errors[e.path as keyof ErrorMessageTypes] = e.message;
               }
            });
         }
         setErrorMessage(errors);
      }
   };

   const saveApiCall = () => {
      setSubmitLoading(true);
      const reqBody = {
         activityName: formData.activityName,
         scheduleDate: formData.scheduleDate,
         activityStartTime: formData.scheduleTime,
         activityId: formData.activityId,
         spOpportunity: formData.spopportunity,
         activityCategoryCode: formData.categoryCode,
         activityFrequency: formData.activityfrequency,
         deityId: formData.deity,
         activityType: formData.activityType,
         activityDescription: formData.activityDescription,
         activityEndTime: null,
         deityGroupId: null,
      };
      apiRequest({
         method: 'POST',
         endpoint: apiPath.addedTempleActivity,
         includeToken: true,
         body: JSON.stringify(reqBody),
      })
         .then((res) => {
            const successMessage = LOCALE.ADDED_ACTIVITY;
            toast.custom(
               <SuccessPopup
                  headerMessage="Daily activity"
                  showMessage={successMessage}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
            );
            setFormData(formData);
            setSubmitLoading(false);
         })
         .catch((err) => {
            toast.custom(
               <ErrorPopup
                  showMessage={catchErrorMessageHandler(err)}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
               {
                  duration: constants.TOAST_POPUP_DURATION,
               },
            );
            setSubmitLoading(false);
         });
   };

   const searchApiCall = () => {
      apiRequest({
         method: 'GET',
         endpoint: apiPath.deitiesList,
         includeToken: true,
      })
         .then((res) => {
            setDeitysName(res.data);
         })
         .catch((err) => {
            toast.custom(
               <ErrorPopup
                  showMessage={catchErrorMessageHandler(err)}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
               {
                  duration: constants.TOAST_POPUP_DURATION,
               },
            );
         });
   };
   const handleFieldChange = (field: any, value: any) => {
      if (field === 'scheduleDate') {
         const formattedDate = dayjs(value, 'YYYY-MM-DD');
         setFormData({
            ...formData,
            [field]: formattedDate.isValid() ? formattedDate.format('YYYY-MM-DD') : '',
         });
         setErrorMessage({ ...errorMessage, [field]: '' });
      } else {
         setErrorMessage({ ...errorMessage, [field]: '' })
         setFormData({
            ...formData,
            [field]: value,
         });
      }
   };

   const handleTypeChange = (event: any) => {
      setErrorMessage({ ...errorMessage, activityType: "" });
      setFormData({ ...formData, activityType: event.target.value });
   };
   const ActivityTypeRadioButtons = (): React.ReactElement => {
      return (
         <>
            <RadioGroup
               sx={{ display: 'flex', flexDirection: 'row', gap: '5px' }}
               value={formData.activityType}
               onChange={handleTypeChange}
            >
               <FormControlLabel
                  value="e" // Value represents the actual value of the radio button
                  control={
                     <Radio
                        sx={{
                           '& .MuiSvgIcon-root': { fontSize: 18, fill: '#f05802' },
                           color: '#F05802',
                        }}
                     />
                  }
                  label="Event"
               />
               <FormControlLabel
                  value="p" // Value represents the actual value of the radio button
                  control={
                     <Radio
                        sx={{
                           '& .MuiSvgIcon-root': { fontSize: 18, fill: '#f05802' },
                           color: '#F05802',
                        }}
                     />
                  }
                  label="Pooja"
               />
            </RadioGroup>
            {errorMessage.activityType ? (
               <Typography sx={{ color: 'red', fontSize: '12px' }}>
                  {errorMessage.activityType}
               </Typography>
            ) : (
               ''
            )}
         </>
      );
   };
   const handleFrequencyChange = (event: any) => {
      setFormData({ ...formData, activityfrequency: event.target.value });
      setErrorMessage({ ...errorMessage, activityfrequency: '' });
   };

   const handleSpoppChange = (event: any) => {
      setFormData({ ...formData, spopportunity: event.target.value });
      setErrorMessage({ ...errorMessage, spopportunity: '' });
   };

   const handleback = () => {
      props.handleBack?.();
      props.setCloseForm?.(true);
   };
   const ActivityFrequencyRadioButtons = (): React.ReactElement => {
      return (
         <RadioGroup
            sx={{ display: 'flex', flexDirection: 'row', gap: '5px', marginBottom: '24px' }}
            value={formData.activityfrequency}
            onChange={handleFrequencyChange} // Add onChange handler
         >
            <FormControlLabel
               value="yes"
               control={
                  <Radio
                     sx={{
                        '& .MuiSvgIcon-root': { fontSize: 18, fill: '#f05802' },
                        color: '#F05802',
                     }}
                  />
               }
               label="Yes"
            />
            <FormControlLabel
               value="no"
               control={
                  <Radio
                     sx={{
                        '& .MuiSvgIcon-root': { fontSize: 18, fill: '#f05802' },
                        color: '#F05802',
                     }}
                  />
               }
               label="No"
            />
            {errorMessage.activityfrequency ? (
               <Typography sx={{ color: 'red', fontSize: '12px' }}>
                  {errorMessage.activityfrequency}
               </Typography>
            ) : (
               ''
            )}
         </RadioGroup>
      );
   };
   const SpooprtunityRadioButtons = (): React.ReactElement => {
      return (
         <RadioGroup
            sx={{ display: 'flex', flexDirection: 'row', gap: '5px', marginBottom: '24px' }}
            value={formData.spopportunity}
            onChange={handleSpoppChange} // Add onChange handler
         >
            <FormControlLabel
               value="y"
               control={
                  <Radio
                     sx={{
                        '& .MuiSvgIcon-root': { fontSize: 18, fill: '#f05802' },
                        color: '#F05802',
                     }}
                  />
               }
               label="Yes"
            />
            <FormControlLabel
               value="n"
               control={
                  <Radio
                     sx={{
                        '& .MuiSvgIcon-root': { fontSize: 18, fill: '#f05802' },
                        color: '#F05802',
                     }}
                  />
               }
               label="No"
            />
            {errorMessage.spopportunity ? (
               <Typography sx={{ color: 'red', fontSize: '12px' }}>
                  {errorMessage.spopportunity}
               </Typography>
            ) : (
               ''
            )}
         </RadioGroup>
      );
   };

   const ScheduleDate = (): React.ReactElement => {
      return (
         <div style={{ minWidth: '100%', borderRadius: '8px' }}>
            <StyledDateContainer sx={{ borderRadius: '8px', background: '#FFF9E4' }}>
               <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DateTimePicker']}>
                     <CustomDatePicker
                        slots={{
                           openPickerIcon: CustomCalendarTodayOutlinedIcon,
                        }}
                        slotProps={{
                           inputAdornment: {
                              position: 'end',
                           },
                        }}
                        format="MM-DD-YYYY"
                        value={
                           formData.scheduleDate ? dayjs(formData.scheduleDate, 'MM-DD-YYYY') : ''

                        }
                        onChange={(value) => {
                           setErrorMessage({ ...errorMessage, scheduleDate: '' });
                           handleFieldChange('scheduleDate', value);
                        }}
                        className={
                           errorMessage.scheduleDate ? styles.errorTextField : styles.textField
                        }
                     />
                  </DemoContainer>
               </LocalizationProvider>
            </StyledDateContainer>
            {errorMessage.scheduleDate ? (
               <Typography sx={{ color: 'red', fontSize: '12px' }}>
                  {errorMessage.scheduleDate}
               </Typography>
            ) : (
               ''
            )}
         </div>
      );
   };
   const Diety = (): React.ReactElement => {
      return (
         <div style={{ minWidth: '100%', borderRadius: '8px' }}>
            <FormControl sx={{ width: '100%' }}>
               <CustomSelect
                  className={errorMessage.deity ? styles.errorTextField : styles.textField}
                  IconComponent={KeyboardArrowDownIcon}
                  value={formData.deity}
                  defaultValue=""
                  id="grouped-select"
                  MenuProps={{
                     PaperProps: {
                        sx: {
                           border: '1px solid #F05802',
                           '& ul': {
                              paddingTop: 0,
                              paddingBottom: 0,
                              overflowY: 'auto',
                              minHeight: 'auto',
                              maxHeight: '300px',
                              '&::-webkit-scrollbar': {
                                 width: '8px',
                              },
                              '&::-webkit-scrollbar-thumb': {
                                 background: '#F3C539',
                                 borderRadius: '6px',
                              },
                           },
                        },
                     },
                  }}
                  onChange={(e) => {
                     setErrorMessage({ ...errorMessage, deity: '' });
                     handleFieldChange('deity', e.target.value);
                  }}
               >
                  {deitysName.map((item: any, index: number) => (
                     <CustomMenuItem key={index} value={item.deityName}>
                        {item.deityName}
                     </CustomMenuItem>
                  ))}
               </CustomSelect>
            </FormControl>
            {errorMessage.deity ? (
               <Typography sx={{ color: 'red', fontSize: '12px' }}>{errorMessage.deity}</Typography>
            ) : (
               ''
            )}
         </div>
      );
   };
   const handleReset = () => {
      updateData();
   };

   const handleCancel = () => {
      setFormData({
         ...formFields,
      });
   };

   const handleKeyPress = (e: any) => {
      if (e.key === 'Enter') {
         e.preventDefault(); // Prevent the default form submission behavior
         if (selectedData?.id) {
            handleUpdate();
         } else {
            handleSave();
         }

      }
   };


   const handleButtons = () => {
      return (
         <div>
            {!isNil(selectedData?.id) ? (
               <>
                  <button className={commonStyles.cancelButton} onClick={handleReset}>
                     Reset
                  </button>
                  <CommonButton className={commonStyles.saveButton} onClick={handleUpdate}>
                     {submitLoading ? (
                        <>
                           <ClipLoader size={20} color="#fff" />
                           <span style={{ marginLeft: '8px' }}>Update</span>
                        </>
                     ) : (
                        'Update'
                     )}
                  </CommonButton>
               </>
            ) : (
               <>
                  <button className={commonStyles.cancelButton} onClick={handleCancel}>
                     Reset
                  </button>
                  <CommonButton className={commonStyles.saveButton} onClick={handleSave}>
                     {submitLoading ? (
                        <>
                           <ClipLoader size={20} color="#fff" />
                           <span style={{ marginLeft: '8px' }}>Add</span>
                        </>
                     ) : (
                        'Add'
                     )}
                  </CommonButton>
               </>
            )}
         </div>
      );
   };

   const validateActivityName = (value: string) => {
      const regex = /^[a-zA-Z0-9\s]*$/;
      return regex.test(value);
   };
   return !isAdding ? (
      <>
         <Toaster />
         <Box className={styles.totalLayout}>
            <Grid sx={{ display: "flex" }} onClick={handleback}>
               <span style={{
                  marginTop: "6px",
                  marginRight: "7px"
               }}>
                  <CustomLeftIcon />
               </span>
               <Typography className={styles.SubHeading}>Daily Activities</Typography>
            </Grid>

            <form className={styles.formLayout} onKeyDown={handleKeyPress}>
               <Grid container spacing={2}>
                  <Grid xs={12} sm={4} item className={styles.singleFeild}>
                     {CustomRequiredInputLabel('Activity Name', true)}
                     <CustomTextFeild
                        fullWidth
                        required
                        value={formData.activityName}
                        className={
                           errorMessage.activityName ? styles.errorTextField : styles.textField
                        }
                        onChange={(e) => {
                           const value = e.target.value;
                           if (validateActivityName(value)) {
                              setErrorMessage({ ...errorMessage, activityName: '' });
                              handleFieldChange('activityName', value);
                           }
                        }}
                     />
                     {errorMessage.activityName ? (
                        <Typography sx={{ color: 'red', fontSize: '12px' }}>
                           {errorMessage.activityName}
                        </Typography>
                     ) : (
                        ''
                     )}
                  </Grid>
               </Grid>

               <Grid container spacing={2}>
                  <Grid xs={12} sm={4} item className={styles.singleFeild}>
                     {CustomRequiredInputLabel('Activity Id', true)}
                     <CustomTextFeild
                        fullWidth
                        required
                        value={formData.activityId}
                        className={
                           errorMessage.activityId ? styles.errorTextField : styles.textField
                        }
                        onChange={(e) => {
                           setErrorMessage({ ...errorMessage, activityId: '' });
                           handleFieldChange('activityId', e.target.value);
                        }}
                     />
                     {errorMessage.activityId ? (
                        <Typography sx={{ color: 'red', fontSize: '12px' }}>
                           {errorMessage.activityId}
                        </Typography>
                     ) : (
                        ''
                     )}
                  </Grid>
                  <Grid xs={12} sm={4} item className={styles.singleFeild}>
                     {CustomRequiredInputLabel('Category Code', true)}
                     <CustomTextFeild
                        fullWidth
                        required
                        value={formData.categoryCode}
                        className={
                           errorMessage.categoryCode ? styles.errorTextField : styles.textField
                        }
                        onChange={(e) => {
                           setErrorMessage({ ...errorMessage, categoryCode: '' });
                           handleFieldChange('categoryCode', e.target.value);
                        }}
                     />
                     {errorMessage.categoryCode ? (
                        <Typography sx={{ color: 'red', fontSize: '12px' }}>
                           {errorMessage.categoryCode}
                        </Typography>
                     ) : (
                        ''
                     )}
                  </Grid>
               </Grid>
               <Grid container spacing={2}>
                  <Grid xs={12} sm={4} item className={styles.singleFeild}>
                     {CustomRequiredInputLabel('Schedule Date', true)}
                     {ScheduleDate()}
                  </Grid>
                  <Grid xs={12} sm={4} item className={styles.singleFeild}>
                     <HcccTimePicker
                        handleFieldChange={handleFieldChange}
                        labelName="Schedule Time"
                        fieldName="scheduleTime"
                        className={
                           errorMessage.scheduleTime ? styles.errorTextField : styles.textField
                        }
                        value={formData.scheduleTime}
                     />
                     {errorMessage.scheduleTime ? (
                        <Typography sx={{ color: 'red', fontSize: '12px' }}>
                           {errorMessage.scheduleTime}
                        </Typography>
                     ) : (
                        ''
                     )}
                  </Grid>
               </Grid>
               <Grid className={styles.singleFeild}>
                  {CustomRequiredInputLabel('Activity Type', true)}
                  {ActivityTypeRadioButtons()}
               </Grid>
               <Grid container spacing={2}>
                  <Grid xs={12} sm={4} item className={styles.singleFeild}>
                     {CustomRequiredInputLabel('Activity Description', true)}
                     <CustomTextFeild
                        fullWidth
                        required
                        value={formData.activityDescription}
                        className={
                           errorMessage.activityDescription
                              ? styles.errorTextField
                              : styles.textField
                        }
                        onChange={(e) => {
                           setErrorMessage({ ...errorMessage, activityDescription: '' });
                           handleFieldChange('activityDescription', e.target.value);
                        }}
                     />
                     {errorMessage.activityDescription ? (
                        <Typography sx={{ color: 'red', fontSize: '12px' }}>
                           {errorMessage.activityDescription}
                        </Typography>
                     ) : (
                        ''
                     )}
                  </Grid>
                  <Grid xs={12} sm={4} item className={styles.singleFeild}>
                     {CustomRequiredInputLabel('Deity', true)}
                     {Diety()}
                  </Grid>
               </Grid>
               <Grid
                  container
                  spacing={2}
                  className={styles.singleFeild}
                  style={{ padding: '24px' }}
               >
                  <Grid xs={12} sm={4}>
                     {CustomRequiredInputLabel(' Activity Frequency', true)}
                     {ActivityFrequencyRadioButtons()}
                  </Grid>
                  <Grid xs={12} sm={4}>
                     {CustomRequiredInputLabel('SP Opportunity', true)}
                     {SpooprtunityRadioButtons()}
                  </Grid>
               </Grid>
            </form>
            {handleButtons()}
         </Box>
      </>
   ) : (
      <DailyActivityInformation />
   );
};

export default DailyActivitiesForm;
